import React, { lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './contexts/AuthContext';
import Layout from './components/Layout/Layout';
import ErrorBoundary from './components/UI/ErrorBoundary';
import SectionErrorBoundary from './components/UI/SectionErrorBoundary';
import MascotteLoader from './components/UI/MascotteLoader';
import LandingPage from './components/Pages/LandingPage';
import './styles/utilities.css';
import './App.css';

// Auth (lazy — LandingPage est le vrai point d'entree)
const Login = lazy(() => import('./components/Auth/Login'));
const Register = lazy(() => import('./components/Auth/Register'));

// Auth secondaire (lazy)
const ForgotPassword = lazy(() => import('./components/Auth/ForgotPassword'));
const ResetPassword = lazy(() => import('./components/Auth/ResetPassword'));
const VerifyEmail = lazy(() => import('./components/Auth/VerifyEmail'));

// Pages legales (lazy)
const CGV = lazy(() => import('./components/Pages/Legal/CGV'));
const PolitiqueConfidentialite = lazy(() => import('./components/Pages/Legal/PolitiqueConfidentialite'));
const PolitiqueRemboursement = lazy(() => import('./components/Pages/Legal/PolitiqueRemboursement'));
const MentionsLegales = lazy(() => import('./components/Pages/Legal/MentionsLegales'));
const SuppressionDonnees = lazy(() => import('./components/Pages/Legal/SuppressionDonnees'));

// Dashboard
const DashboardParent = lazy(() => import('./components/Dashboard/Parent/DashboardParent'));

// Pages communes (lazy)
const EnfantsList = lazy(() => import('./components/Enfants/EnfantsList'));
const EnfantForm = lazy(() => import('./components/Enfants/EnfantForm'));
const EnfantDetail = lazy(() => import('./components/Enfants/EnfantDetail'));
const Messages = lazy(() => import('./components/Messages/Messages'));
const Profil = lazy(() => import('./components/Profil/Profil'));
const NotificationsPage = lazy(() => import('./components/Notifications/NotificationsPage'));
const Premium = lazy(() => import('./components/Pages/Premium'));
const PremiumSuccess = lazy(() => import('./components/Pages/PremiumSuccess'));
const PremiumCancel = lazy(() => import('./components/Pages/PremiumCancel'));
const CoffreIndex = lazy(() => import('./components/Coffre/CoffreIndex'));
const CoffrePage = lazy(() => import('./components/Coffre/CoffrePage'));
const PhenixPage = lazy(() => import('./components/Phenix/PhenixPage'));
const AdminPanel = lazy(() => import('./components/Admin/AdminPanel'));
const SeedTool = lazy(() => import('./components/SeedTool/SeedTool'));
const AcceptInvitation = lazy(() => import('./components/Invitations/AcceptInvitation'));
const NotFound = lazy(() => import('./components/Pages/NotFound'));

// Route privee
const PrivateRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  if (loading) return <MascotteLoader />;
  return isAuthenticated ? children : <Navigate to="/login" />;
};

// Route publique (redirige si connecte, honore ?redirect=)
const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  if (loading) return <MascotteLoader />;
  if (isAuthenticated) {
    const params = new URLSearchParams(window.location.search);
    const redirect = params.get('redirect');
    return <Navigate to={redirect || '/dashboard'} />;
  }
  return children;
};

const AppRoutes = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  return (
    <Routes>
      <Route path="/dashboard" element={<DashboardParent />} />
      <Route path="/" element={<Navigate to={isAdmin ? '/admin' : '/dashboard'} replace />} />

      {/* Enfants */}
      <Route path="/enfants" element={<EnfantsList />} />
      <Route path="/enfants/nouveau" element={<EnfantForm />} />
      <Route path="/enfants/:id" element={<EnfantDetail />} />

      {/* Feed */}
      {/* /publications redirige vers dashboard (le feed est integre) */}
      <Route path="/publications" element={<Navigate to="/dashboard" />} />

      {/* Coffre */}
      <Route path="/coffre" element={<CoffreIndex />} />
      <Route path="/coffre/:enfantId" element={<CoffrePage />} />

      {/* Phenix */}
      <Route path="/phenix" element={<PhenixPage />} />

      {/* Messages */}
      <Route path="/messages" element={<Messages />} />

      {/* Invitations integrees dans /enfants — redirection */}
      <Route path="/invitations" element={<Navigate to="/enfants" />} />
      <Route path="/notifications" element={<NotificationsPage />} />

      {/* Premium */}
      <Route path="/premium" element={<Premium />} />
      <Route path="/premium/success" element={<PremiumSuccess />} />
      <Route path="/premium/cancel" element={<PremiumCancel />} />

      {/* Profil */}
      <Route path="/profil" element={<Profil />} />

      {/* Seed Tool */}
      <Route path="/seed-tool" element={<SeedTool />} />

      {/* Admin */}
      {isAdmin && <Route path="/admin" element={<AdminPanel />} />}

      <Route path="*" element={<NotFound />} />
    </Routes>
  );
};

function App() {
  return (
    <ErrorBoundary>
      <Router>
        <div className="App">
          <Suspense fallback={<MascotteLoader />}>
            <Routes>
              {/* Landing page publique */}
              <Route path="/" element={<PublicRoute><LandingPage /></PublicRoute>} />

              {/* Routes publiques */}
              <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
              <Route path="/register" element={<PublicRoute><Register /></PublicRoute>} />
              <Route path="/forgot-password" element={<PublicRoute><ForgotPassword /></PublicRoute>} />
              <Route path="/reset-password/:token" element={<PublicRoute><ResetPassword /></PublicRoute>} />
              <Route path="/verify-email/:token" element={<VerifyEmail />} />
              <Route path="/invitation/:token" element={<AcceptInvitation />} />

              {/* Pages legales */}
              <Route path="/cgv" element={<CGV />} />
              <Route path="/confidentialite" element={<PolitiqueConfidentialite />} />
              <Route path="/remboursement" element={<PolitiqueRemboursement />} />
              <Route path="/mentions-legales" element={<MentionsLegales />} />
              <Route path="/suppression-donnees" element={<SuppressionDonnees />} />

              {/* Routes privees */}
              <Route path="/*" element={
                <PrivateRoute>
                  <Layout>
                    <SectionErrorBoundary section="Contenu">
                      <AppRoutes />
                    </SectionErrorBoundary>
                  </Layout>
                </PrivateRoute>
              } />
            </Routes>
          </Suspense>
        </div>
      </Router>
    </ErrorBoundary>
  );
}

export default App;
